import { Injectable } from "@angular/core";
import {
  Client,
  PackageResponse,
  PackageRequest,
  PackageTypeEnum,
  WorkshopSystemEnum,
  SwaggerException,
  ErrorResponse
} from "src/domain/client";
import { FormService } from "./form.service";
import { SelectedProposalService } from "../interfaces/interfaces";
import { forkJoin, from, Observable, of, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

export interface PackageRequestData {
  chassiNumber: string;
  dealerNumber: string;
  odometer: number;
  systems: WorkshopSystemEnum[];
}

@Injectable({
  providedIn: "root"
})
export class PackageService {
  public enabledServiceTypes: PackageTypeEnum[] = [
    PackageTypeEnum.OriginalService,
    PackageTypeEnum.BaseService
  ];

  constructor(private formService: FormService) {}

  public refreshEnabledServiceTypes(
    data: PackageRequestData
  ): Observable<PackageTypeEnum[]> {
    return from(
      new Client().apiV2PackageDetermineServiceType(new PackageRequest(data))
    );
  }

  public getOriginalService(): Promise<PackageResponse> {
    return new Client().apiV2PackageOriginalService(
      new PackageRequest({
        chassiNumber: this.formService.carInfoResponse.chassiNumber,
        dealerNumber: this.formService.selectedWorkshop.number,
        odometer: this.formService.aboutVehicleForm.controls.milage?.value * 10,
        systems: this.formService.serviceTypeSelection
      })
    );
  }

  public getWheelPackages(): Promise<PackageResponse[]> {
    return new Client().apiV2PackageWheel(
      new PackageRequest({
        chassiNumber: this.formService.carInfoResponse.chassiNumber,
        dealerNumber: this.formService.selectedWorkshop.number,
        odometer: this.formService.aboutVehicleForm.controls.milage?.value * 10,
        systems: this.formService.serviceTypeSelection
      })
    );
  }

  public getDropOffPackages(data: PackageRequestData) {
    return new Client().apiV2PackageDropOffOption(
      new PackageRequest({
        chassiNumber: data.chassiNumber,
        dealerNumber: data.dealerNumber,
        odometer: data.odometer * 10,
        systems: data.systems
      })
    );
  }

  private handleGetPackageError(
    err: SwaggerException | ErrorResponse
  ): Observable<PackageResponse[]> {
    if (err.status === 404) {
      return of([]);
    }
    return throwError(err);
  }

  public getAllPackagesForStep4(
    data: PackageRequestData,
    enabledServiceTypes: PackageTypeEnum[]
  ): Observable<PackageResponse[]> {
    const allObservables: Observable<PackageResponse[] | PackageResponse>[] =
      [];
    const client = new Client();

    const baseRequest = new PackageRequest({
      chassiNumber: data.chassiNumber,
      dealerNumber: data.dealerNumber,
      odometer: data.odometer * 10,
      systems: data.systems
    });

    allObservables.push(
      from(client.apiV2PackageDropOffOption(baseRequest)).pipe(
        catchError(this.handleGetPackageError)
      )
    );

    if (data.systems.includes(WorkshopSystemEnum.Service)) {
      if (enabledServiceTypes?.includes(PackageTypeEnum.BaseService))
        allObservables.push(
          from(client.apiV2PackageBaseServiceSuggestions(baseRequest)).pipe(
            catchError(this.handleGetPackageError)
          )
        );
      if (enabledServiceTypes?.includes(PackageTypeEnum.OriginalService))
        allObservables.push(
          from(client.apiV2PackageOriginalService(baseRequest)).pipe(
            catchError(this.handleGetPackageError)
          )
        );
    }
    if (data.systems.includes(WorkshopSystemEnum.Windscreen)) {
      allObservables.push(
        from(client.apiV2PackageWindshield(baseRequest)).pipe(
          catchError(this.handleGetPackageError)
        )
      );
    }
    if (data.systems.includes(WorkshopSystemEnum.Wheel)) {
      allObservables.push(
        from(client.apiV2PackageWheel(baseRequest)).pipe(
          catchError(this.handleGetPackageError)
        )
      );
    }
    if (data.systems.includes(WorkshopSystemEnum.Troubleshooting)) {
      allObservables.push(
        from(client.apiV2PackageTroubleshooting(baseRequest)).pipe(
          catchError(this.handleGetPackageError)
        )
      );
    }
    if (data.systems.includes(WorkshopSystemEnum.TechUpdate)) {
      allObservables.push(
        from(client.apiV2PackageTechUpdate(baseRequest)).pipe(
          catchError(this.handleGetPackageError)
        )
      );
    }

    return forkJoin(allObservables).pipe(
      map((results) => ([] as PackageResponse[]).concat(...results))
    );
  }
}
